import { NgModule } from '@angular/core';
import { angularProvidersBuilder } from '@harmony/angular';
import { Firestore } from 'firebase/firestore';
import { Functions } from 'firebase/functions';
import { FirebaseToken } from '../../app/core/firebase.module';
import { AuthProvider } from '../auth/auth.provider';
import { AcceptInviteInteractor } from './domain/interactors/accept-invite.interactor';
import { AddTeamMemberInteractor } from './domain/interactors/add-team-member.interactor';
import { CreateTeamInteractor } from './domain/interactors/create-team.interactor';
import { GetTeamByIdInteractor } from './domain/interactors/get-team-by-id.interactor';
import { GetTeamUsersInteractor } from './domain/interactors/get-team-users.interactor';
import { LeaveTeamInteractor } from './domain/interactors/leave-team.interactor';
import { UpdateUserInteractor } from './domain/interactors/update-user.interactor';
import { RemoveTeamMemberInteractor } from './domain/interactors/remove-team-member.interactor';
import { TeamDefaultProvider, TeamProvider } from './team.provider';
import { UpdateTeamInteractor } from './domain/interactors/update-team.interactor';
import { CanEnableDomainInteractor } from './domain/interactors/can-enable-domain.interactor';
import { DisableDomainInteractor } from './domain/interactors/disable-domain.interactor';
import { EnableDomainInteractor } from './domain/interactors/enable-domain.interactor';
import { GetTeamSizeInteractor } from './domain/interactors/get-team-size.interactor';
import { UpdateTeamSettingsInteractor } from './domain/interactors/update-team-settings.interactor';
import { CreateTeamGroupInteractor } from './domain/interactors/create-team-group.interactor';
import { GetTeamGroupsInteractor } from './domain/interactors/get-team-groups.interactor';
import { UpdateTeamGroupInteractor } from './domain/interactors/update-team-group.interactor';
import { DeleteTeamGroupInteractor } from './domain/interactors/delete-team-group.interactor';
import { SyncTeamGroupUsersInteractor } from './domain/interactors/sync-team-group-users.interactor';
import { AddTeamMembersInteractor } from './domain/interactors/add-team-members.interactor';
import { CreateTeamMicrosoftSitesInteractor } from './domain/interactors/create-team-microsoft-sites.interactor';
import { GetTeamPublicCacheByIdInteractor } from './domain/interactors/get-team-public-cache-by-id.interactor';
import { LeaveTeamDevInteractor } from './domain/interactors/leave-team-dev.interactor';
import { TEAM_GET_CURRENT$ } from '../../app/core/core.module';
import { Subject } from 'rxjs';
import { Team } from '@uploadcam/common';
import { CreateTeamTagInteractor } from './domain/interactors/create-team-tag.interactor';
import { UpdateUserLocalInteractor } from './domain/interactors/update-user-local.interactor';
import { CommonProvider } from '../common/common.provider';
import { OperationLogsProvider } from '../operation-logs/operation-logs.provider';
import { ChangeTeamRootInteractor } from './domain/interactors/change-team-root.interactor';
import { GetTeamTagsInteractor } from './domain/interactors/get-team-tags.interactor';
import { UpdateTeamTagInteractor } from './domain/interactors/update-team-tag.interactor';

@NgModule({
  providers: angularProvidersBuilder({
    provide: TeamProvider,
    deps: [
      AuthProvider,
      CommonProvider,
      FirebaseToken.Firestore,
      FirebaseToken.Functions,
      OperationLogsProvider,
      TEAM_GET_CURRENT$,
    ],
    useFactory: (
      authProvider: AuthProvider,
      common: CommonProvider,
      firestore: Firestore,
      functions: Functions,
      operationLogsProvider: OperationLogsProvider,
      teamGetCurrent$: Subject<Team>,
    ) =>
      new TeamDefaultProvider(
        authProvider,
        common,
        firestore,
        functions,
        operationLogsProvider,
        teamGetCurrent$,
      ),
  })
    .add(AcceptInviteInteractor, (p) => p.provideAcceptInvite())
    .add(AddTeamMemberInteractor, (p) => p.provideAddTeamMember())
    .add(AddTeamMembersInteractor, (p) => p.provideAddTeamMembers())
    .add(CanEnableDomainInteractor, (p) => p.provideCanEnableDomain())
    .add(ChangeTeamRootInteractor, (p) => p.provideChangeTeamRoot())
    .add(CreateTeamGroupInteractor, (p) => p.provideCreateTeamGroup())
    .add(CreateTeamInteractor, (p) => p.provideCreateTeam())
    .add(CreateTeamMicrosoftSitesInteractor, (p) => p.provideCreateTeamMicrosoftSites())
    .add(DeleteTeamGroupInteractor, (p) => p.provideDeleteTeamGroup())
    .add(DisableDomainInteractor, (p) => p.provideDisableDomain())
    .add(EnableDomainInteractor, (p) => p.provideEnableDomain())
    .add(GetTeamByIdInteractor, (p) => p.provideGetTeamById())
    .add(GetTeamGroupsInteractor, (p) => p.provideGetTeamGroups())
    .add(GetTeamSizeInteractor, (p) => p.provideGetTeamSize())
    .add(GetTeamPublicCacheByIdInteractor, (p) => p.provideGetTeamPublicCacheById())
    .add(GetTeamTagsInteractor, (p) => p.provideGetTeamTags())
    .add(GetTeamUsersInteractor, (p) => p.provideGetTeamUsers())
    .add(LeaveTeamInteractor, (p) => p.provideLeaveTeam())
    .add(LeaveTeamDevInteractor, (p) => p.provideLeaveTeamDev())
    .add(RemoveTeamMemberInteractor, (p) => p.provideRemoveTeamMember())
    .add(CreateTeamTagInteractor, (p) => p.provideSetTeamTag())
    .add(SyncTeamGroupUsersInteractor, (p) => p.provideSyncTeamGroupUsers())
    .add(UpdateTeamGroupInteractor, (p) => p.provideUpdateTeamGroup())
    .add(UpdateTeamInteractor, (p) => p.provideUpdateTeam())
    .add(UpdateTeamSettingsInteractor, (p) => p.provideUpdateTeamSettings())
    .add(UpdateTeamTagInteractor, (p) => p.provideUpdateTeamTag())
    .add(UpdateUserInteractor, (p) => p.provideUpdateUser())
    .add(UpdateUserLocalInteractor, (p) => p.provideUpdateUserLocal())
    .build(),
})
export class TeamProviderModule {}
